import type { TerminalServerMessage } from '@this+that/models';
import log from 'loglevel';

import { nextBackoffMs } from './backoff';
import { createListeners } from './createListeners';
import {
  type TerminalDevice,
  type TerminalGateway,
  TerminalSendError,
  type TerminalTransport,
} from './types';
import {
  TerminalConnectionStatus,
  TerminalSendFailure,
  TerminalTransportKind,
} from '../../../enums/terminal';
import {
  TERMINAL_LOCAL_AUTH_FRAME_TYPE,
  TERMINAL_LOCAL_HTTP_BASE,
  TERMINAL_LOCAL_MAX_FAILURES_BEFORE_OFFLINE,
  TERMINAL_LOCAL_PING_PATH,
  TERMINAL_LOCAL_PING_TIMEOUT_MS,
  TERMINAL_LOCAL_SOCKET_PATH,
  TERMINAL_LOCAL_WS_BASE,
} from '../../../utils/constants/terminalConstants';

const ADDRESS_SPACES: ReadonlyArray<string | undefined> = [
  'loopback',
  'local',
  undefined,
];

function buildProbeRequest(signal: AbortSignal): Request {
  const url = `${TERMINAL_LOCAL_HTTP_BASE}${TERMINAL_LOCAL_PING_PATH}`;
  for (const targetAddressSpace of ADDRESS_SPACES) {
    try {
      return new Request(url, {
        signal,
        ...(targetAddressSpace ? { targetAddressSpace } : {}),
      } as RequestInit);
    } catch {
      // This browser rejects that address-space value; try the next one.
    }
  }
  return new Request(url, { signal });
}

export async function probeLocalDaemon(
  timeoutMs: number = TERMINAL_LOCAL_PING_TIMEOUT_MS,
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetchImpl(buildProbeRequest(controller.signal));
    if (!response.ok) {
      log.info(`terminal: local ping answered HTTP ${response.status}`);
    }
    return response.ok;
  } catch (error) {
    const reason = controller.signal.aborted
      ? `no answer within ${timeoutMs}ms (a Local Network Access prompt also counts)`
      : error instanceof Error
        ? error.message
        : String(error);
    log.info(`terminal: local ping failed: ${reason}`);
    return false;
  } finally {
    clearTimeout(timer);
  }
}

export function parseServerFrame(raw: unknown): TerminalServerMessage | null {
  if (typeof raw !== 'string') {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as { type?: unknown };
    return typeof parsed?.type === 'string'
      ? (parsed as TerminalServerMessage)
      : null;
  } catch {
    return null;
  }
}

export function createLocalTransport(
  gateway: TerminalGateway,
  device: TerminalDevice,
): TerminalTransport {
  const messages = createListeners<TerminalServerMessage>();
  const statuses = createListeners<TerminalConnectionStatus>();
  let socket: WebSocket | null = null;
  let isClosed = false;
  let failures = 0;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;

  const scheduleRetry = () => {
    retryTimer = setTimeout(
      () => {
        retryTimer = null;
        void attempt();
      },
      nextBackoffMs(failures - 1),
    );
  };

  const handleFailure = () => {
    if (isClosed) {
      return;
    }
    failures += 1;
    const isOffline = failures >= TERMINAL_LOCAL_MAX_FAILURES_BEFORE_OFFLINE;
    statuses.emit(
      isOffline
        ? TerminalConnectionStatus.OFFLINE
        : TerminalConnectionStatus.RECONNECTING,
    );
    scheduleRetry();
  };

  const openSocket = (ticket: string) => {
    const ws = new WebSocket(
      `${TERMINAL_LOCAL_WS_BASE}${TERMINAL_LOCAL_SOCKET_PATH}`,
    );
    socket = ws;
    ws.onopen = () => {
      ws.send(JSON.stringify({ type: TERMINAL_LOCAL_AUTH_FRAME_TYPE, ticket }));
      statuses.emit(TerminalConnectionStatus.ONLINE);
    };
    ws.onmessage = (event) => {
      const message = parseServerFrame(event.data);
      if (message) {
        failures = 0;
        messages.emit(message);
      }
    };
    ws.onclose = () => {
      if (socket === ws) {
        socket = null;
        handleFailure();
      }
    };
    ws.onerror = () => undefined;
  };

  async function attempt(): Promise<void> {
    if (isClosed) {
      return;
    }
    try {
      const ticket = await gateway.createTicket(device.deviceId);
      if (!isClosed) {
        openSocket(ticket);
      }
    } catch (error) {
      log.debug('localTransport: ticket failed', error);
      handleFailure();
    }
  }

  return {
    connect: () => {
      statuses.emit(TerminalConnectionStatus.CONNECTING);
      void attempt();
    },
    send: async (message) => {
      if (socket?.readyState !== WebSocket.OPEN) {
        throw new TerminalSendError(TerminalSendFailure.DEVICE_OFFLINE);
      }
      socket.send(JSON.stringify(message));
    },
    onMessage: messages.subscribe,
    onStatus: statuses.subscribe,
    getKind: () => TerminalTransportKind.LOCAL,
    getDeviceId: () => device.deviceId,
    close: () => {
      isClosed = true;
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
      const current = socket;
      socket = null;
      current?.close();
      messages.clear();
      statuses.clear();
    },
  };
}
